import { motion } from 'framer-motion';
import { FlaskConical, Radar, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import logoLight from '../../assets/brand/cyberelites-light.png';
import logoDark from '../../assets/brand/cyberelites-dark.png';

const NAVY = '#0a1424';
const NAVY_2 = '#0f2036';
const GREEN = '#16c65a';

/**
 * Shared split-screen chrome for the unauthenticated pages (login, forgot
 * password). Left: branded panel (light logo at full size, headline, feature
 * chips, hex grid + glows). Right: centred form column with the mobile logo.
 *
 * Pages pass only their form as `children`, so login and reset are visually
 * identical — same background, same logo treatment. Previously the reset page
 * hand-rolled a bare card with the wrong (dark-on-dark, 34px) logo; extracting
 * this shell is what keeps the two from drifting again.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen w-full lg:grid-cols-[1.1fr_1fr]">
      {/* ---------- Left brand panel (desktop) ---------- */}
      <aside
        className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between"
        style={{ background: `radial-gradient(1200px 600px at 15% -10%, ${NAVY_2}, ${NAVY} 55%)`, padding: '48px 56px' }}
      >
        <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', top: '-8%', left: '-6%', width: 520, height: 520, borderRadius: '50%', background: `${GREEN}22`, filter: 'blur(150px)' }} />
          <div style={{ position: 'absolute', bottom: '-14%', right: '-10%', width: 460, height: 460, borderRadius: '50%', background: '#1e40af22', filter: 'blur(150px)' }} />
          <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
            <defs>
              <pattern id="authhex" width="46" height="40" patternUnits="userSpaceOnUse">
                <path d="M23 0 L46 13 L46 27 L23 40 L0 27 L0 13 Z" fill="none" stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#authhex)" />
          </svg>
        </div>

        <motion.img
          src={logoLight}
          alt="CyberElites"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ position: 'relative', width: 230, height: 'auto', objectFit: 'contain', marginLeft: -18 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          style={{ position: 'relative', maxWidth: 460 }}
        >
          <h2 style={{ color: '#fff', fontSize: 34, lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.02em' }}>
            Train like the adversary.<br />
            <span style={{ color: GREEN }}>Defend like an elite.</span>
          </h2>
          <p style={{ color: '#9fb0c7', fontSize: 15, lineHeight: 1.6, marginTop: 16 }}>
            Hands-on cyber range for SOC analysts and defenders — live attack scenarios,
            real security tooling, and graded labs on isolated infrastructure.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 26 }}>
            {[
              { icon: FlaskConical, label: '102 hands-on labs' },
              { icon: Radar, label: 'Live attack scenarios' },
              { icon: ShieldCheck, label: 'Real SOC tooling' },
            ].map(({ icon: Icon, label }) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, background: '#ffffff0d', border: '1px solid #ffffff14', color: '#cdd9e8', fontSize: 12.5, fontWeight: 500 }}>
                <Icon size={14} color={GREEN} /> {label}
              </span>
            ))}
          </div>
        </motion.div>

        <p style={{ position: 'relative', color: '#63758c', fontSize: 12 }}>© {new Date().getFullYear()} CyberElites — Cyber Range Platform</p>
      </aside>

      {/* ---------- Right form panel ---------- */}
      <main className="relative flex items-center justify-center bg-void px-5 py-12 sm:px-8">
        <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 size-[360px] -translate-x-1/2 rounded-full bg-neon/5 blur-[130px] lg:hidden" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-[380px]"
        >
          {/* mobile logo (left panel hidden below lg) */}
          <img src={logoDark} alt="CyberElites" className="mb-8 h-auto w-44 object-contain object-left dark:hidden lg:hidden" style={{ marginLeft: -6 }} />
          <img src={logoLight} alt="CyberElites" className="mb-8 hidden h-auto w-44 object-contain object-left dark:block lg:!hidden" style={{ marginLeft: -6 }} />
          {children}
        </motion.div>
      </main>
    </div>
  );
}

/** Tailwind class string for auth form inputs — shared so login and reset match. */
export const authInputCls =
  'w-full rounded-xl border border-bd bg-input-bg px-3.5 py-3 text-[14px] text-txt outline-none ' +
  'transition-colors duration-150 placeholder:text-txt-muted focus:border-neon/60 focus:ring-2 focus:ring-neon/15 disabled:opacity-50';
